import { useCallback } from "react";
import type { SubagentRunView } from "@lumen/shared";
import {
  applyConversationStreamEvent,
  isAbortError,
  streamRequest,
} from "./ChatPage.utils";
import type { ChatActionsContext } from "./ChatContextTypes";

export function useSubagentContinueAction({
  activeRequest,
  applyConversationSnapshot,
  conversationRef,
  generationMessageId,
  loadApprovals,
  mergeLatestMessages,
  onAttention,
  refreshConversations,
  setConversation,
  setError,
  setSending,
  updateConversation,
}: ChatActionsContext) {
  return useCallback(
    async (run: SubagentRunView) => {
      const current = conversationRef.current;
      if (!current || current.id !== run.conversationId) return;
      setSending(true);
      setError("");
      const controller = new AbortController();
      activeRequest.current = controller;
      try {
        await streamRequest(
          `/conversations/${run.conversationId}/subagent-runs/${run.id}/continue/stream`,
          {},
          (event) => {
            applyConversationStreamEvent(event, {
              updateConversation,
              setConversation,
              assistantMessageId: generationMessageId,
              applyConversationSnapshot,
              conversationId: run.conversationId,
              mergeLatestMessages,
            });
            if (event.type === "done") {
              void loadApprovals();
            }
            if (event.type === "error") {
              throw new Error(event.error);
            }
          },
          controller.signal,
          {
            isStale: () => conversationRef.current?.id !== run.conversationId,
            onAttention,
            notifyOnDone: true,
          },
        );
        await Promise.all([loadApprovals(), refreshConversations()]);
      } catch (reason) {
        if (isAbortError(reason)) return;
        setError(
          reason instanceof Error
            ? reason.message
            : "Could not continue the subagent",
        );
      } finally {
        if (activeRequest.current === controller) activeRequest.current = null;
        if (conversationRef.current?.id === run.conversationId)
          setSending(false);
      }
    },
    [
      activeRequest,
      applyConversationSnapshot,
      conversationRef,
      generationMessageId,
      loadApprovals,
      mergeLatestMessages,
      onAttention,
      refreshConversations,
      setConversation,
      setError,
      setSending,
      updateConversation,
    ],
  );
}
